import { Cookies } from "lib/cookie-monster";
import { VehicleData } from "components/VDP/VehicleDetailWrapper/types";
import { Auth } from "services/auth/contexts";
import { FavoriteAd } from "components/FavouritesList/types";
import { getCarListings } from "lib/analytics/mapResultsToListings";
import { ResultsProps } from "components/SRP/elements/Results";
import {
    RequestMeta,
    CarouselEventNames,
    ShareEventNames,
    ContactEventNames,
    SortingProps,
    ViewType
} from "./types";
import { mapProductToOffering } from "./mapProductToOffering";
import { extractBrowserAndVersion } from "./extractBrowserAndVersion";
import { mapProductToFavourite } from "./mapProductToFavourite";

import { pushEventToContext, productIdOnlyEvent } from "./datalayerHelpers";

export function pageView(requestMeta: RequestMeta): void {
    const {
        title,
        host,
        path,
        query,
        userAgent,
        browserLanguage,
        pageType,
        viewport,
        device,
        orientation,
        envName
    } = requestMeta;
    const browser = extractBrowserAndVersion(userAgent);

    pushEventToContext("pageView", {
        request: {
            pageType, // Page type (e.g. home)
            pageTitle: title, // Page title (e.g. Home)
            urlFull: path + query, // Page path including querystring
            urlPath: path, // Page path (e.g. /mijn-autotrack)
            urlHostname: host, // URL hostname (e.g. www.autotrack.nl)
            timestamp: Date.now(), // Time stamp (e.g. 1616718667)
            testVariant: "" // Test variant for e.g. A/B testing (e.g. at_unknown)
        },
        environment: {
            device, // Device of visitor (e.g. Smartphone | Tablet | Desktop)
            orientation,
            environment: envName, // Environment server (e.g. prod)
            viewPort: viewport,
            browserName: browser.name, // Browser of visitor (e.g. Google Chrome | DuckDuckGo | Safari)
            browserVersion: browser.version, // Version of browser (e.g. 83.0.4103.97)
            country: "", // Country of visitor (e.g. Netherlands) ?? leave empty for now. requires extra API call.
            languageEnvironment: "nl", // Language of the environment (e.g. nl)
            languageBrowser: browserLanguage, // Language code of the environment (e.g. nl-NL | nl-BE)
            bot: "" // Name of bot scraping the website (e.g. Googlebot)
        }
    });
}

export function authenticationStatusUpdated(user: Auth, cookies: Cookies): void {
    pushEventToContext("authenticationStatusUpdated", {
        user: {
            analyticsID: cookies._gid || "", // Analytics Client ID of user (e.g. 111136958.2)
            country: "", // Country of user (e.g. Netherlands) -- leave empty for now. requires extra API call.
            cXenseID: cookies.cX_S || "", // cXense ID (e.g. 1234567)
            hasAdBlocker: false, // Whether the user is using an Ad Blocker (e.g. true | false)
            pwID: "", // Sourcepoint ID (e.g. 1234567) -- not available yet
            source: "", // Source of user (e.g. www.facebook.com) -- leave empty if unknown
            spID: "", // Snowplow ID of user (e.g. 1234567) -- not available yet
            userCreationDate: "", // Creation date of user ID (e.g. 2020-12-10T14:53:40Z)
            userId: user.userId || "", // User ID (e.g. 3349129)
            userLogin: user.token ? "ingelogd" : "niet ingelogd", // Login status of user (e.g. ingelogd | niet ingelogd)
            userType: "", // b2b | b2c (e.g. b2b | b2c) -- leave empty
            visitNumber: -1 // Number of unique visits (e.g. 1)
        }
    });
}

export function savedSearcClick(): void {
    pushEventToContext("savedSearch", { metaData: "add" });
}

export function offeringsRetrieved(products: VehicleData[]): void {
    const mappedProducts =
        products?.map((p, i) => mapProductToOffering(p, i, "currentOfferings")) || [];

    pushEventToContext("currentOfferingsRetrieved", {
        currentOfferings: {
            advertisement: { ...mappedProducts }
        }
    });
}

export function favouritesRetrieved(products: FavoriteAd[], sortOrder: string): void {
    const mappedProducts = products?.map((p, i) => mapProductToFavourite(p, i)) || [];

    pushEventToContext("favoritesRetrieved", {
        favoriteListing: {
            count: products.length ?? 0,
            sortedBy: sortOrder,
            advertisement: { ...mappedProducts }
        }
    });
}

export function savedSearcEvent(): void {
    pushEventToContext("savedSearch", { metaData: "add" });
}

/**
 * Event to indicate the removal of a favorite ad from 'Mijn Autotrack',
 * but provided that it has already been removed from the Search Service Listings API
 */
export function favoriteRemoveNoAdEvent(id: number, title: string): void {
    pushEventToContext("favoriteRemove", {
        selectedOffer: {
            adDetails: {
                advertisementID: id,
                name: title,
                publishDate: "no longer published"
            }
        }
    });
}

export function saveAccountSettingsEvent(): void {
    pushEventToContext("saveAccountSettingsEvent", {});
}

export function deleteAccountEvent(): void {
    pushEventToContext("deleteAccount", {});
}

// providerName#unknown necessary for favourites
export function selectOffering({
    product,
    position,
    providerName
}: {
    product: VehicleData;
    position?: number;
    providerName: "other" | "unknown";
}): void {
    pushEventToContext("clickOnCar", {
        selectedOffer: { advertisement: mapProductToOffering(product, position), providerName }
    });
}

export function contactEvent({
    id,
    contactType,
    providerName
}: {
    id: number;
    contactType: ContactEventNames;
    providerName: "other" | "unknown";
}): void {
    productIdOnlyEvent({ eventName: "contact", metadata: contactType, id, providerName });
}

export function favoriteAdd(id: number): void {
    productIdOnlyEvent({ eventName: "favorite", metadata: "add", id, providerName: "unknown" });
}

export function favoriteRemove(id: number): void {
    productIdOnlyEvent({ eventName: "favorite", metadata: "remove", id, providerName: "unknown" });
}

export function shareEvent({
    metadata,
    id,
    providerName
}: {
    metadata: ShareEventNames;
    id?: number;
    providerName: "other" | "unknown";
}): void {
    productIdOnlyEvent({ eventName: "share", metadata, id, providerName });
}

export function carouselEvent(metadata: CarouselEventNames): void {
    productIdOnlyEvent({ eventName: "carrouselRetrieved", metadata, providerName: "unknown" }); // too many usages of carouselEvent to find providerName
}

export function selectedSortingEvent({ sortedBy, listView }: SortingProps): void {
    const metadata = {
        selectedSorting: {
            sortedBy: sortedBy.toLowerCase(),
            viewType: listView ? "lijst" : "foto's"
        }
    };
    pushEventToContext("selectedSortingChanged", metadata);
}

export function carRetrievedEvent(
    hits: ResultsProps["results"]["hits"],
    counts: ResultsProps["counts"],
    searchParams: ResultsProps["searchParams"],
    viewType: ViewType
): void {
    pushEventToContext("carListingRetrieved", getCarListings(hits, counts, searchParams, viewType));
}

export function searchEvent(searchFilters: ResultsProps["searchFilters"]): void {
    pushEventToContext("search", { search: searchFilters });
}
